import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { isAxiosError } from "axios";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { PageHeader, StatStrip } from "@/components/ui/page-header";
import { TableSkeleton } from "@/components/ui/page-skeleton";
import { Skeleton } from "@/components/ui/skeleton";
import { useApp } from "@/hooks/use-app-context";
import { useLanguages } from "@/hooks/use-languages";
import { usePrivateAudioPlayer } from "@/hooks/use-private-audio";
import { useToast } from "@/hooks/use-toast";
import { Icon } from "@/lib/icons";
import { cn } from "@/lib/utils";
import { ContributionHistory } from "./ContributionHistory";
import { LanguageBreakdown } from "./LanguageBreakdown";
import { MonthlyFlow } from "./MonthlyFlow";
import { languageRows, lastMonths } from "./contribution-history";
import { myContributionsCopy } from "./my-contributions-copy";
import { useMyContributions } from "./use-my-contributions";

/**
 * "My contributions": the figures that matter, audio per language, the
 * monthly trend and, at the heart of the page, the full history with audio
 * playback. The road to a payout lives in "My earnings", linked from here.
 */
export function MyContributionsPage({ lang }: { lang: string }) {
  const { t, user } = useApp();
  const { toast } = useToast();
  const tModel = t.myContributionsPage;
  const copy = myContributionsCopy[lang === "en" ? "en" : "fr"];
  const locale = lang === "en" ? "en-GB" : "fr-FR";
  const base = `/${lang}`;
  const { stats, situation, refresh, refreshing } = useMyContributions(user?.id ? String(user.id) : undefined);
  const { languages } = useLanguages();

  const number = useCallback((value: number) => new Intl.NumberFormat(locale).format(value), [locale]);
  const names = useMemo(() => {
    const map = new Map<string, string>();
    for (const entry of languages) {
      const code = (entry.code ?? "").trim().toLowerCase();
      if (code) map.set(code, entry.displayName || entry.name || entry.code);
    }
    return map;
  }, [languages]);
  const languageName = useCallback(
    (code: string | null) => (code ? names.get(code) ?? code.toUpperCase() : copy.unknownLanguage),
    [names, copy.unknownLanguage],
  );

  // G2 PCA: audio is read through the private endpoint, and only on click.
  // The hook owns the playing state, so the button reflects the real playback
  // and a second click stops the recording instead of stacking a new one.
  const { playBlob, stop, isPlaying, playingBlobName } = usePrivateAudioPlayer();
  const play = useCallback(
    async (blobName: string) => {
      if (isPlaying(blobName)) {
        stop();
        return;
      }
      try {
        await playBlob(blobName);
      } catch (error) {
        const forbidden = isAxiosError(error) && error.response?.status === 403;
        toast({
          title: tModel.audioPlayErrorTitle,
          description: forbidden ? tModel.audioPlayForbidden : tModel.audioPlayError,
          variant: "destructive",
        });
      }
    },
    [isPlaying, playBlob, stop, toast, tModel],
  );

  const s = stats.data;
  const perLanguage = useMemo(() => languageRows(s?.languages), [s?.languages]);
  const months = useMemo(() => lastMonths(s?.flow, 6), [s?.flow]);
  const hasFlow = months.some((m) => m.accepted + m.rejected + m.pending > 0);

  const contributions = s?.global?.contributionsTotal ?? s?.global?.total ?? 0;
  // Validated audio from the payout situation: the audio status is set by the
  // community votes, and the payout counts the same votes, so both agree in
  // production. No fallback to the stats, or the figure would flip on load.
  const accepted = situation.data?.acceptedAudioCount ?? 0;
  const pending = s?.audio?.uploaded ?? 0;
  const rejected = s?.audio?.rejected ?? 0;
  const transcriptions = s?.transcription?.submitted ?? 0;
  const v = s?.validation;
  const reviews = (v?.validatedAudio ?? 0) + (v?.rejectedAudio ?? 0) + (v?.validatedTranscriptions ?? 0) + (v?.rejectedTranscriptions ?? 0);
  const isEmpty = Boolean(s) && contributions === 0 && pending === 0 && accepted === 0 && rejected === 0 && transcriptions === 0 && reviews === 0;

  const contribute = (
    <Button asChild size="sm">
      <Link to={`${base}/speak-contribute`}>
        <Icon name="mic" />
        {tModel.ctaContribute}
      </Link>
    </Button>
  );

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8 px-4 pb-16 pt-8 sm:px-6 lg:px-8">
      <PageHeader
        title={tModel.title}
        description={copy.description}
        actions={
          <Button variant="outline" size="sm" onClick={() => void refresh()} disabled={stats.isLoading || refreshing}>
            <Icon name="refreshCw" className={cn(refreshing && "animate-spin")} />
            {tModel.refresh}
          </Button>
        }
      />

      {stats.isLoading ? (
        <div className="space-y-8" aria-busy="true">
          <Skeleton className="h-[74px] bg-muted/40" />
          <TableSkeleton rows={5} />
        </div>
      ) : stats.isError ? (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-surface px-4 py-3 text-sm text-muted-foreground">
          {copy.loadError}
          <Button size="sm" variant="outline" onClick={() => void stats.refetch()}>
            {tModel.retry}
          </Button>
        </div>
      ) : isEmpty ? (
        <div className="rounded-lg border border-border/60 bg-surface">
          <EmptyState icon="mic" title={tModel.emptyTitle} description={tModel.emptyDescription} action={contribute} />
        </div>
      ) : (
        <>
          <StatStrip
            className="sm:grid-cols-3 lg:grid-cols-6"
            items={[
              { label: copy.figContributions, value: number(contributions) },
              { label: copy.figAccepted, value: situation.isLoading ? "…" : number(accepted), tone: accepted > 0 ? "success" : "default" },
              { label: copy.figPending, value: number(pending), tone: pending > 0 ? "attention" : "default", hint: copy.figPendingHint },
              { label: copy.figRejected, value: number(rejected), tone: rejected > 0 ? "danger" : "default" },
              { label: copy.figTranscriptions, value: number(transcriptions) },
              { label: copy.figReviews, value: number(reviews) },
            ]}
          />

          <div className="flex flex-col gap-3 rounded-lg border border-border/60 bg-surface px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="flex items-center gap-2.5 text-sm text-muted-foreground">
              <Icon name="wallet" className="size-4 shrink-0 text-brand" />
              {copy.earningsText}
            </p>
            <Button asChild size="sm" variant="outline" className="self-start sm:self-auto">
              <Link to={`${base}/get-paid?section=overview`}>
                {tModel.journeyCtaDetails}
                <Icon name="arrowRight" />
              </Link>
            </Button>
          </div>

          {perLanguage.length > 0 || hasFlow ? (
            <div className={cn("grid gap-6", perLanguage.length > 0 && hasFlow && "lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]")}>
              {perLanguage.length > 0 ? <LanguageBreakdown copy={copy} rows={perLanguage} languageName={languageName} format={number} /> : null}
              {hasFlow ? <MonthlyFlow copy={copy} months={months} locale={locale} format={number} /> : null}
            </div>
          ) : null}

          {contributions > 0 ? (
            <ContributionHistory
              copy={copy}
              userId={user?.id ? String(user.id) : undefined}
              languages={perLanguage.flatMap((row) => (row.code ? [row.code] : []))}
              languageName={languageName}
              locale={locale}
              format={number}
              rangeLabel={tModel.paginationRange}
              playLabel={tModel.playAudio}
              playingBlob={playingBlobName}
              onPlay={(blobName) => void play(blobName)}
            />
          ) : (
            <section aria-labelledby="mc-history-empty" className="space-y-3">
              <h2 id="mc-history-empty" className="text-sm font-medium text-muted-foreground">
                {copy.historyTitle}
              </h2>
              <div className="rounded-lg border border-border/60 bg-surface">
                <EmptyState compact icon="inbox" title={tModel.noActivity} action={contribute} />
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
